import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import { Button, ActionButtonSpinner, getActionButtonLoadingClassName } from '@ui';
import { cityFromStationAddress } from '../../utils/ukraineRegions';
import { resizeImageToJpeg, submitServiceRequest } from '../../hookServices/feedbackApi';
import styles from './ServiceRequestModal.module.css';

const DESC_MIN = 5;
const DESC_MAX = 3000;
const PHONE_RE = /^\+?[0-9 ()-]{9,20}$/;
const PROBLEM_TYPES = ['port_not_working', 'station_offline', 'cable_damage', 'payment', 'other'];
const PHOTO_SLOTS = [
  { kind: 'car_port', field: 'photoCarPort' },
  { kind: 'car', field: 'photoCar' },
  { kind: 'station', field: 'photoStation' },
];
const FIELD_ANCHOR = {
  problemTypes: 'sr-problems',
  port: 'sr-port',
  description: 'sr-description',
  phone: 'sr-phone',
  car_port: 'sr-photo-slot-car_port',
  car: 'sr-photo-slot-car',
  station: 'sr-photo-slot-station',
};

const emptyPhotos = () => ({
  car_port: { file: null, preview: null },
  car: { file: null, preview: null },
  station: { file: null, preview: null },
});

function readSavedPhone() {
  try {
    return localStorage.getItem('servicePhone') || '';
  } catch {
    return '';
  }
}

function savePhone(phone) {
  try {
    localStorage.setItem('servicePhone', phone);
  } catch {
    /* ignore */
  }
}

export default function ServiceRequestModal({
  modalOpen,
  setModalOpen,
  station,
  stationNumber,
  clientId,
  onSubmitted,
}) {
  const { t } = useTranslation();
  const fileRefs = useRef({});
  const [port, setPort] = useState(String(stationNumber || ''));
  const [city, setCity] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [photos, setPhotos] = useState(emptyPhotos);
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  function clearFieldError(key) {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function collectFieldErrors() {
    const errors = {};
    if (selectedTypes.length === 0) errors.problemTypes = 'problemRequired';
    if (!String(port || '').trim()) errors.port = 'portRequired';
    if (description.trim().length < DESC_MIN) errors.description = 'descriptionRequired';
    const phoneTrim = phone.trim();
    if (!phoneTrim) errors.phone = 'phoneRequired';
    else if (!PHONE_RE.test(phoneTrim)) errors.phone = 'invalidPhone';
    PHOTO_SLOTS.forEach(({ kind }) => {
      if (!photos[kind].file) errors[kind] = 'photoRequired';
    });
    return errors;
  }

  useEffect(() => {
    if (!modalOpen) return undefined;
    setPort(String(stationNumber || ''));
    setCity(cityFromStationAddress(station?.address) || '');
    setSelectedTypes([]);
    setDescription('');
    setPhone(readSavedPhone());
    setPhotos(prev => {
      Object.values(prev).forEach(p => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
      return emptyPhotos();
    });
    setErrorKey(null);
    setFieldErrors({});
    setSuccess(false);
    setSubmitting(false);
    return undefined;
  }, [modalOpen, stationNumber, station?.address]);

  useEffect(() => {
    return () => {
      Object.values(photos).forEach(p => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleType(key) {
    setSelectedTypes(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    clearFieldError('problemTypes');
  }

  function handlePhotoChange(kind, event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotos(prev => {
      const next = { ...prev };
      if (next[kind]?.preview) URL.revokeObjectURL(next[kind].preview);
      next[kind] = { file, preview: URL.createObjectURL(file) };
      return next;
    });
    clearFieldError(kind);
  }

  function clearPhoto(kind) {
    setPhotos(prev => {
      const next = { ...prev };
      if (next[kind]?.preview) URL.revokeObjectURL(next[kind].preview);
      next[kind] = { file: null, preview: null };
      return next;
    });
    if (fileRefs.current[kind]) fileRefs.current[kind].value = '';
  }

  async function handleSubmit() {
    if (submitting) return;
    const errors = collectFieldErrors();
    setFieldErrors(errors);
    const firstInvalid = Object.keys(FIELD_ANCHOR).find(key => errors[key]);
    if (firstInvalid) {
      queueMicrotask(() => {
        document.getElementById(FIELD_ANCHOR[firstInvalid])?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }
    setSubmitting(true);
    setErrorKey(null);
    try {
      const photoBlobs = {};
      for (const { kind, field } of PHOTO_SLOTS) {
        const file = photos[kind]?.file;
        if (file) {
          photoBlobs[field] = await resizeImageToJpeg(file);
        }
      }
      await submitServiceRequest({
        stationNumber: String(port).trim(),
        clientId,
        city: city.trim() || null,
        problemTypes: selectedTypes,
        description: description.trim(),
        phone: phone.trim(),
        ...photoBlobs,
      });
      savePhone(phone.trim());
      ReactGA.event({
        category: 'service_request',
        action: 'submit',
        label: String(port || ''),
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      const detail = err?.detail || err?.message || '';
      if (err?.status === 429 || detail === 'RATE_LIMITED') {
        setErrorKey('rateLimited');
      } else if (detail === 'INVALID_PHONE') {
        setErrorKey('invalidPhone');
      } else if (err?.status === 413 || detail === 'BODY_TOO_LARGE') {
        setErrorKey('tooLarge');
      } else if (detail === 'PHOTOS_REQUIRED') {
        setErrorKey('photosRequired');
      } else {
        setErrorKey('generic');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} scrollable>
      <div className={styles.root}>
        <button type="button" className={styles.close} aria-label={t('close')} onClick={() => setModalOpen(false)}>
          ×
        </button>

        {success ? (
          <>
            <h2 className={styles.title}>{t('serviceRequestForm.successTitle')}</h2>
            <p className={styles.successBody}>{t('serviceRequestForm.successBody')}</p>
            <Button onClick={() => setModalOpen(false)} className={styles.submit} style={{ marginTop: '16px' }}>
              <span>OK</span>
            </Button>
          </>
        ) : (
          <>
            <h2 className={styles.title}>{t('serviceRequestForm.title')}</h2>

            <div className={styles.label}>{t('serviceRequestForm.problemTypesLabel')}</div>
            <div
              id="sr-problems"
              className={`${styles.chips} ${fieldErrors.problemTypes ? styles.chipsInvalid : ''}`}
              role="group"
              aria-label={t('serviceRequestForm.problemTypesLabel')}
            >
              {PROBLEM_TYPES.map(key => {
                const active = selectedTypes.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                    aria-pressed={active}
                    onClick={() => toggleType(key)}
                  >
                    {t(`serviceRequestForm.problemTypes.${key}`)}
                  </button>
                );
              })}
            </div>
            {fieldErrors.problemTypes ? (
              <p className={styles.fieldError}>{t(`serviceRequestForm.errors.${fieldErrors.problemTypes}`)}</p>
            ) : null}

            <label className={styles.label} htmlFor="sr-port">
              {t('serviceRequestForm.portLabel')}
            </label>
            <input
              id="sr-port"
              className={`${styles.input} ${fieldErrors.port ? styles.inputInvalid : ''}`}
              type="text"
              value={port}
              onChange={e => {
                setPort(e.target.value.slice(0, 64));
                clearFieldError('port');
              }}
            />
            {fieldErrors.port ? (
              <p className={styles.fieldError}>{t(`serviceRequestForm.errors.${fieldErrors.port}`)}</p>
            ) : null}

            <label className={styles.label} htmlFor="sr-city">
              {t('serviceRequestForm.cityLabel')} ({t('serviceRequestForm.optional')})
            </label>
            <input
              id="sr-city"
              className={styles.input}
              type="text"
              value={city}
              onChange={e => setCity(e.target.value.slice(0, 128))}
              placeholder={t('serviceRequestForm.cityPlaceholder')}
            />

            <label className={styles.label} htmlFor="sr-description">
              {t('serviceRequestForm.descriptionLabel')}
            </label>
            <textarea
              id="sr-description"
              className={`${styles.textarea} ${fieldErrors.description ? styles.inputInvalid : ''}`}
              value={description}
              onChange={e => {
                setDescription(e.target.value.slice(0, DESC_MAX));
                clearFieldError('description');
              }}
              rows={4}
              placeholder={t('serviceRequestForm.descriptionPlaceholder')}
            />
            <p className={styles.hint}>{t('serviceRequestForm.descriptionHint')}</p>
            <div className={styles.counter}>
              {description.trim().length}/{DESC_MAX}
            </div>
            {fieldErrors.description ? (
              <p className={styles.fieldError}>{t(`serviceRequestForm.errors.${fieldErrors.description}`)}</p>
            ) : null}

            <label className={styles.label} htmlFor="sr-phone">
              {t('serviceRequestForm.phoneLabel')}
            </label>
            <input
              id="sr-phone"
              className={`${styles.input} ${fieldErrors.phone ? styles.inputInvalid : ''}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={e => {
                setPhone(e.target.value.slice(0, 32));
                clearFieldError('phone');
              }}
              placeholder={t('serviceRequestForm.phonePlaceholder')}
            />
            {fieldErrors.phone ? (
              <p className={styles.fieldError}>{t(`serviceRequestForm.errors.${fieldErrors.phone}`)}</p>
            ) : null}

            <div className={styles.label}>{t('serviceRequestForm.photosLabel')}</div>
            {PHOTO_SLOTS.map(({ kind }) => (
              <div key={kind} id={`sr-photo-slot-${kind}`} className={styles.photoSlot}>
                <input
                  id={`sr-photo-${kind}`}
                  ref={el => {
                    fileRefs.current[kind] = el;
                  }}
                  className={styles.fileInput}
                  type="file"
                  accept="image/*"
                  onChange={e => handlePhotoChange(kind, e)}
                />
                <label
                  className={`${styles.fileBtn} ${fieldErrors[kind] ? styles.fileBtnInvalid : ''}`}
                  htmlFor={`sr-photo-${kind}`}
                >
                  {photos[kind].file ? photos[kind].file.name : t(`serviceRequestForm.photoSlots.${kind}`)}
                </label>
                {fieldErrors[kind] ? (
                  <p className={styles.photoError}>{t(`serviceRequestForm.errors.${fieldErrors[kind]}`)}</p>
                ) : null}
                {photos[kind].preview && (
                  <div className={styles.preview}>
                    <img className={styles.previewImg} src={photos[kind].preview} alt="" />
                    <Button type="button" variant="outline-alt" onClick={() => clearPhoto(kind)}>
                      {t('serviceRequestForm.removePhoto')}
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {errorKey && <p className={styles.error}>{t(`serviceRequestForm.errors.${errorKey}`)}</p>}

            <Button
              type="button"
              className={`${getActionButtonLoadingClassName(submitting)} ${styles.submit}`}
              disabled={submitting}
              aria-busy={submitting}
              onClick={handleSubmit}
            >
              {submitting ? <ActionButtonSpinner /> : <span>{t('serviceRequestForm.submit')}</span>}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
